<script setup lang="ts">
import type { NuxtError } from '#app';
import { t } from '@/i18n';
import { localizedPath, siteLocaleFromPath } from '@/lib/site-locale';

const props = defineProps<{
  error: NuxtError;
}>();

const isDev = computed(() => import.meta.dev);

const is404 = computed(() => props.error?.statusCode === 404);

const requestUrl = useRequestURL();
const errorLocale = computed(() => siteLocaleFromPath(requestUrl.pathname) ?? 'en');
const homePath = computed(() => localizedPath(errorLocale.value, '/'));
const handleError = () => clearError({ redirect: homePath.value });

// 开发模式下展开/收起错误详情
const showDetails = ref(true);

useHead({
  title: () =>
    is404.value ? t('landingPages.error.seo.title_404') : t('landingPages.error.seo.title_generic'),
});
</script>

<template>
  <div class="error-container">
    <!-- Animated Background Elements -->
    <div class="bg-elements">
      <div class="bg-circle bg-circle-1"></div>
      <div class="bg-circle bg-circle-2"></div>
      <div class="bg-line bg-line-1"></div>
      <div class="bg-line bg-line-2"></div>
      <div class="bg-line bg-line-3"></div>
    </div>

    <!-- Main Content -->
    <main class="main-content">
      <!-- Logo -->
      <div class="logo-wrapper">
        <NuxtLink :to="homePath" class="logo-link">
          <img src="/veesoai-black.svg" alt="Veeso AI" class="logo block dark:hidden" />
          <img src="/veesoai-light.svg" alt="Veeso AI" class="logo hidden dark:block" />
        </NuxtLink>
      </div>

      <!-- Error Section -->
      <div class="error-section">
        <!-- Error Code with Glitch Effect -->
        <div class="error-code-wrapper">
          <span class="error-code" data-text="404">
            {{ error?.statusCode || '404' }}
          </span>
        </div>

        <!-- Error Message -->
        <div class="error-message">
          <h1 v-if="is404" class="error-title">
            <span class="title-line">{{ t('landingPages.error.title_404_line1') }}</span>
            <span class="title-line title-accent">{{
              t('landingPages.error.title_404_line2')
            }}</span>
          </h1>
          <h1 v-else class="error-title">
            <span class="title-line">{{ t('landingPages.error.title_generic_line1') }}</span>
            <span class="title-line title-accent">{{
              t('landingPages.error.title_generic_line2')
            }}</span>
          </h1>

          <p class="error-description">
            <template v-if="is404">
              {{ t('landingPages.error.desc_404') }}
            </template>
            <template v-else>
              {{ error?.message || t('landingPages.error.desc_generic_fallback') }}
            </template>
          </p>
        </div>

        <!-- 开发模式：错误详情 -->
        <div v-if="isDev && !is404 && error" class="dev-error-details">
          <button class="toggle-details" @click="showDetails = !showDetails">
            <span class="toggle-icon">{{ showDetails ? '▼' : '▶' }}</span>
            <span>{{ t('landingPages.error.dev.toggle') }}</span>
          </button>

          <div v-if="showDetails" class="error-details-content">
            <div class="detail-section">
              <h3 class="detail-title">Status</h3>
              <p class="detail-value">{{ error.statusCode }} {{ error.statusMessage }}</p>
            </div>

            <div v-if="error.message" class="detail-section">
              <h3 class="detail-title">Message</h3>
              <p class="detail-value">{{ error.message }}</p>
            </div>

            <div v-if="error.stack" class="detail-section">
              <h3 class="detail-title">Stack Trace</h3>
              <pre class="detail-stack">{{ error.stack }}</pre>
            </div>

            <div v-if="error.data" class="detail-section">
              <h3 class="detail-title">Error Data</h3>
              <pre class="detail-stack">{{ JSON.stringify(error.data, null, 2) }}</pre>
            </div>

            <div class="detail-section">
              <h3 class="detail-title">Full Error Object</h3>
              <pre class="detail-stack">{{ JSON.stringify(error, null, 2) }}</pre>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <button class="primary-button" @click="handleError">
            <span class="button-text">{{ t('landingPages.error.cta.home') }}</span>
            <span class="button-icon">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <path d="M5 12h14M12 5l7 7-7 7" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </span>
          </button>
        </div>

        <!-- Decorative Elements -->
        <div class="decorative-dots">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer class="footer">
      <p class="footer-text">© 2026 Veeso AI</p>
    </footer>
  </div>
</template>

<style scoped>
.error-container {
  min-height: 100vh;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;
}

/* Animated Background Elements */
.bg-elements {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.bg-circle {
  position: absolute;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.03);
}

.bg-circle-1 {
  width: 600px;
  height: 600px;
  top: -200px;
  right: -200px;
  animation: pulse-slow 8s ease-in-out infinite;
}

.bg-circle-2 {
  width: 400px;
  height: 400px;
  bottom: -100px;
  left: -100px;
  animation: pulse-slow 10s ease-in-out infinite reverse;
}

.bg-line {
  position: absolute;
  background: linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.02), transparent);
  height: 1px;
  width: 100%;
}

.bg-line-1 {
  top: 25%;
  animation: slide-right 20s linear infinite;
}

.bg-line-2 {
  top: 50%;
  animation: slide-left 25s linear infinite;
}

.bg-line-3 {
  top: 75%;
  animation: slide-right 30s linear infinite;
}

/* Main Content */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  position: relative;
  z-index: 10;
  gap: 48px;
}

/* Logo */
.logo-wrapper {
  animation: fade-in-down 0.6s ease-out;
}

.logo-link {
  display: inline-block;
  transition: opacity 0.2s ease;
}

.logo-link:hover {
  opacity: 0.7;
}

.logo {
  height: 24px;
  width: auto;
}

/* Error Section */
.error-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 32px;
  animation: fade-in-up 0.8s ease-out 0.2s both;
}

/* Error Code */
.error-code-wrapper {
  position: relative;
}

.error-code {
  font-size: clamp(120px, 25vw, 200px);
  font-weight: 200;
  line-height: 1;
  letter-spacing: -0.05em;
  color: rgba(0, 0, 0, 0.06);
  position: relative;
  font-variant-numeric: tabular-nums;
}

/* Error Message */
.error-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.error-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.title-line {
  font-size: clamp(32px, 8vw, 48px);
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: #000000;
}

.title-accent {
  font-weight: 300;
  color: rgba(0, 0, 0, 0.5);
}

.error-description {
  max-width: 380px;
  font-size: 15px;
  line-height: 1.7;
  color: rgba(0, 0, 0, 0.45);
  font-weight: 300;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 16px;
  margin-top: 8px;
}

.primary-button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  font-size: 14px;
  font-weight: 400;
  color: #000000;
  text-decoration: none;
  border: 1px solid rgba(0, 0, 0, 0.15);
  background: transparent;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: 0.02em;
  position: relative;
  overflow: hidden;
}

.primary-button::before {
  content: '';
  position: absolute;
  inset: 0;
  background: #000000;
  transform: scaleX(0);
  transform-origin: right;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.primary-button:hover::before {
  transform: scaleX(1);
  transform-origin: left;
}

.primary-button:hover {
  border-color: #000000;
  color: #ffffff;
}

.button-text,
.button-icon {
  position: relative;
  z-index: 1;
  transition: transform 0.3s ease;
}

.primary-button:hover .button-icon {
  transform: translateX(4px);
}

.button-icon {
  display: flex;
  align-items: center;
}

/* Decorative Dots */
.decorative-dots {
  display: flex;
  gap: 8px;
  margin-top: 24px;
}

.dot {
  width: 4px;
  height: 4px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  animation: dot-pulse 2s ease-in-out infinite;
}

.dot:nth-child(2) {
  animation-delay: 0.2s;
}

.dot:nth-child(3) {
  animation-delay: 0.4s;
}

/* Footer */
.footer {
  position: relative;
  z-index: 10;
  padding: 24px;
  text-align: center;
}

.footer-text {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.3);
  letter-spacing: 0.02em;
}

/* Animations */
@keyframes fade-in-down {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse-slow {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
}

@keyframes slide-right {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(100%);
  }
}

@keyframes slide-left {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(-100%);
  }
}

@keyframes glitch-1 {
  0%,
  100% {
    transform: translate(0);
  }
  20% {
    transform: translate(-2px, 2px);
  }
  40% {
    transform: translate(-2px, -2px);
  }
  60% {
    transform: translate(2px, 2px);
  }
  80% {
    transform: translate(2px, -2px);
  }
}

@keyframes glitch-2 {
  0%,
  100% {
    transform: translate(0);
  }
  20% {
    transform: translate(2px, -2px);
  }
  40% {
    transform: translate(2px, 2px);
  }
  60% {
    transform: translate(-2px, -2px);
  }
  80% {
    transform: translate(-2px, 2px);
  }
}

@keyframes dot-pulse {
  0%,
  100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}

/* Responsive */
@media (max-width: 640px) {
  .main-content {
    padding: 40px 20px;
    gap: 36px;
  }

  .logo {
    height: 20px;
  }

  .error-section {
    gap: 24px;
  }

  .error-description {
    font-size: 14px;
    padding: 0 8px;
  }

  .primary-button {
    padding: 12px 24px;
    font-size: 13px;
  }

  .bg-circle-1 {
    width: 400px;
    height: 400px;
    top: -150px;
    right: -150px;
  }

  .bg-circle-2 {
    width: 300px;
    height: 300px;
    bottom: -100px;
    left: -100px;
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .error-code::before,
  .error-code::after,
  .bg-circle,
  .bg-line,
  .dot {
    animation: none;
  }

  .logo-wrapper,
  .error-section {
    animation: none;
    opacity: 1;
  }
}

/* Dev Error Details */
.dev-error-details {
  width: 100%;
  max-width: 800px;
  margin-top: 24px;
  text-align: left;
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
  background: rgba(254, 242, 242, 0.5);
  overflow: hidden;
}

:root.dark .dev-error-details {
  background: rgba(127, 29, 29, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
}

.toggle-details {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #dc2626;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background 0.2s ease;
}

.toggle-details:hover {
  background: rgba(239, 68, 68, 0.1);
}

.toggle-icon {
  font-size: 10px;
}

.error-details-content {
  padding: 0 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.detail-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #991b1b;
}

:root.dark .detail-title {
  color: #fca5a5;
}

.detail-value {
  font-size: 13px;
  color: #374151;
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Monaco, Consolas, monospace;
  word-break: break-word;
}

:root.dark .detail-value {
  color: #e5e7eb;
}

.detail-stack {
  font-size: 12px;
  line-height: 1.6;
  color: #374151;
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Monaco, Consolas, monospace;
  background: rgba(0, 0, 0, 0.03);
  padding: 12px;
  border-radius: 6px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 300px;
  overflow-y: auto;
  margin: 0;
}

:root.dark .detail-stack {
  background: rgba(255, 255, 255, 0.05);
  color: #e5e7eb;
}
</style>
